import { existsSync, statSync } from "node:fs";
import { join } from "node:path";
import { getAssetCdnPathPrefix, rewriteKnownPublicMediaUrl } from "./cdn-base.js";
import { resolveAssetFileOnDisk } from "./paths.js";

function isDevMediaCacheBustEnabled() {
  return globalThis.process?.env?.NODE_ENV === "development";
}

function websiteRoot() {
  return typeof process.cwd === "function" ? process.cwd() : "";
}

/** @returns {string} */
export function contentRepoRoot() {
  const env = String(globalThis.process?.env?.CONTENT_REPO_ROOT ?? "").trim();
  if (env) return env;
  return join(websiteRoot(), "..", "content");
}

/**
 * Map a site path like `/img/foo.webp` or CDN pathname `/cdn/audio/...` to a file on disk.
 * @param {string} sitePath path starting with `/` (no query)
 * @returns {string | null}
 */
export function resolveMediaFileOnDiskForCacheBust(sitePath) {
  let pathNoQuery = String(sitePath || "").split("?")[0].trim();
  if (!pathNoQuery.startsWith("/")) return null;

  pathNoQuery = rewriteKnownPublicMediaUrl(pathNoQuery);
  if (pathNoQuery.startsWith("http://") || pathNoQuery.startsWith("https://")) {
    try {
      pathNoQuery = new URL(pathNoQuery).pathname;
    } catch {
      return null;
    }
  }

  const prefix = getAssetCdnPathPrefix();
  if (pathNoQuery.startsWith("/cdn/")) {
    pathNoQuery = pathNoQuery.slice("/cdn".length);
  } else if (prefix && pathNoQuery.startsWith(prefix + "/")) {
    pathNoQuery = pathNoQuery.slice(prefix.length);
  }

  const rel = pathNoQuery.replace(/^\/+/, "");
  if (!rel) return null;

  const fromWebsite = resolveAssetFileOnDisk(rel);
  if (fromWebsite) return fromWebsite;

  const contentCdn = join(contentRepoRoot(), "build", "cdn", rel);
  if (existsSync(contentCdn)) return contentCdn;

  const contentAssets = join(contentRepoRoot(), "src", "assets", rel);
  if (existsSync(contentAssets)) return contentAssets;

  return null;
}

/**
 * Append `?v=mtime` in development so browsers pick up replaced CDN/media bytes.
 * @param {string} urlLike
 * @returns {string}
 */
export function appendDevMediaCacheBuster(urlLike) {
  if (!isDevMediaCacheBustEnabled()) return String(urlLike ?? "");

  let trimmed = String(urlLike ?? "").trim();
  if (!trimmed || trimmed.startsWith("data:")) return trimmed;

  /** @type {URL | null} */
  let parsed = null;
  let pathPart = trimmed;
  const isAbsolute = trimmed.startsWith("http://") || trimmed.startsWith("https://");

  if (isAbsolute) {
    try {
      parsed = new URL(trimmed);
      pathPart = parsed.pathname;
    } catch {
      return trimmed;
    }
  } else {
    trimmed = rewriteKnownPublicMediaUrl(trimmed);
    pathPart = trimmed.split("#")[0].split("?")[0];
  }

  const abs = resolveMediaFileOnDiskForCacheBust(pathPart);
  if (!abs) return isAbsolute && parsed ? parsed.toString() : trimmed;

  try {
    const v = String(Math.floor(statSync(abs).mtimeMs || 0));
    if (parsed) {
      parsed.searchParams.set("v", v);
      return parsed.toString();
    }
    const hashIdx = trimmed.indexOf("#");
    const hash = hashIdx >= 0 ? trimmed.slice(hashIdx) : "";
    const noHash = hashIdx >= 0 ? trimmed.slice(0, hashIdx) : trimmed;
    const qIdx = noHash.indexOf("?");
    const base = qIdx >= 0 ? noHash.slice(0, qIdx) : noHash;
    const params = new URLSearchParams(qIdx >= 0 ? noHash.slice(qIdx + 1) : "");
    params.set("v", v);
    return `${base}?${params.toString()}${hash}`;
  } catch {
    return trimmed;
  }
}
